import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui'
import { useSummary } from '@/modules/account'
import { useStatements } from '@/modules/projects/hooks'
import { isStatementToStart } from '@/modules/projects/status'
import type { Statement } from '@/modules/projects/stores'
import { useDialogActions, useDialogData, useDialogIsOpen } from '@/stores'
import { planTrialRead, sumPages, totalPagesRead } from '../trialCut'
import { FileReadRow } from './FileReadRow'

const dialogId = 'trial-process-confirm'

interface TrialProcessConfirmData {
  statementsToProcess?: Statement[]
  onConfirm: () => void
}

export const TrialProcessConfirmDialog = () => {
  const open = useDialogIsOpen(dialogId)
  const { closeDialog } = useDialogActions()

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && closeDialog()}>
      <DialogContent className="max-w-xl">
        <TrialProcessConfirm />
      </DialogContent>
    </Dialog>
  )
}

TrialProcessConfirmDialog.id = dialogId

const TrialProcessConfirm = () => {
  const data = useDialogData() as TrialProcessConfirmData | undefined
  const { closeDialog } = useDialogActions()
  const pagesLeft = useSummary()?.pagesCredits ?? 0
  const allStatements = useStatements()
  const statements = (data?.statementsToProcess ?? allStatements).filter(
    isStatementToStart,
  )
  const reads = planTrialRead(statements, pagesLeft)
  const pagesRead = totalPagesRead(reads)

  const confirm = () => {
    closeDialog(true)
    data?.onConfirm()
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle>
          Your trial will extract {pagesRead} of {sumPages(statements)} pages
        </DialogTitle>
        <DialogDescription>
          We extract your files in upload order until {pagesLeft} pages are
          used.
        </DialogDescription>
      </DialogHeader>
      <ol className="divide-y rounded-md border">
        {reads.map((fileRead, index) => (
          <FileReadRow
            key={fileRead.statement.id}
            position={index + 1}
            fileRead={fileRead}
          />
        ))}
      </ol>
      <p className="text-[13px] text-muted-foreground">
        Pages not extracted stay in the case and are extracted after you
        upgrade.
      </p>
      <div className="flex justify-end gap-2">
        <Button theme="outline" onClick={() => closeDialog()}>
          Choose different files
        </Button>
        <Button disabled={pagesRead === 0} onClick={confirm}>
          Process {pagesRead} pages
        </Button>
      </div>
    </>
  )
}
